import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Alert, Card, Spinner, Badge } from 'react-bootstrap';
import resultsAlignmentService from '../services/resultsAlignmentService';
import authService from '../services/authService';
import targetMarketService from '../services/targetMarketService';
import businessService from '../services/businessService';
import BackToDashboard from '../components/BackToDashboard';

function ResultsAlignment() {
  const navigate = useNavigate();
  const [stage, setStage] = useState('start'); // start, generating, select, complete
  const [generatedResults, setGeneratedResults] = useState([]);
  const [selectedIndices, setSelectedIndices] = useState([]); // Track which indices are selected
  const [editedTexts, setEditedTexts] = useState({}); // Map of index -> edited text
  const [savedResults, setSavedResults] = useState([]); // The final saved results
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    checkAuthAndLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthAndLoad = async () => {
    // Check authentication
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Check if target market is completed
    const targetResult = await targetMarketService.getPrimaryNiche();
    if (!targetResult.success || !targetResult.data.primary_niche) {
      setError('Please complete Target Market Discovery first');
      return;
    }

    // Check if customer journey is completed (but allow quick-start users with products to proceed)
    const journeyResult = await businessService.getField('customer_personas');
    const hasCustomerPersonas = journeyResult.success && journeyResult.data.field_value;

    // If no customer personas, check if user has products (quick-start path)
    if (!hasCustomerPersonas) {
      try {
        const productsRes = await fetch('/api/products/get-catalog', { credentials: 'include' });
        if (productsRes.ok) {
          const productsData = await productsRes.json();
          if (!productsData.catalog || productsData.catalog.length === 0) {
            setError('Please complete Customer Journey & Personas first');
            return;
          }
          // User has products, allow them to proceed (quick-start path)
        } else {
          setError('Please complete Customer Journey & Personas first');
          return;
        }
      } catch (err) {
        setError('Please complete Customer Journey & Personas first');
        return;
      }
    }

    // Load existing results if any
    const result = await resultsAlignmentService.getResults();
    if (result.success && result.data.results && result.data.results.length > 0) {
      setSavedResults(result.data.results);
      setStage('complete');
    }
  };

  const handleGenerate = async () => {
    setLoading(true);
    setError('');
    setStage('generating');

    const result = await resultsAlignmentService.generateResults();

    setLoading(false);

    if (result.success) {
      setGeneratedResults(result.data.results);
      setSelectedIndices([]);
      setEditedTexts({});
      setStage('select');
    } else {
      setError(result.error);
      setStage('start');
    }
  };

  const handleToggleResult = (index) => {
    if (selectedIndices.includes(index)) {
      // Remove from selected
      setSelectedIndices(selectedIndices.filter(i => i !== index));
      const newEdited = { ...editedTexts };
      delete newEdited[index];
      setEditedTexts(newEdited);
    } else {
      // Add to selected (no max limit)
      setSelectedIndices([...selectedIndices, index]);
    }
    setError('');
  };

  const handleEditResult = (index, newText) => {
    setEditedTexts({
      ...editedTexts,
      [index]: newText
    });
  };

  const handleSave = async () => {
    if (selectedIndices.length < 1) {
      setError('Please select at least 1 result');
      return;
    }

    setLoading(true);
    setError('');

    // Build final results from selected indices
    const finalResults = selectedIndices.map(index => {
      const result = generatedResults[index];
      // Use edited text if available, otherwise use original
      return editedTexts[index] !== undefined ? editedTexts[index] : result.result;
    });

    const result = await resultsAlignmentService.saveResults(finalResults);

    setLoading(false);

    if (result.success) {
      setSavedResults(finalResults);
      setStage('complete');
    } else {
      setError(result.error);
    }
  };

  const handleRegenerate = () => {
    setStage('start');
    setGeneratedResults([]);
    setSelectedIndices([]);
    setEditedTexts({});
  };

  const renderStart = () => (
    <Card className="shadow">
      <Card.Body className="p-4">
        <h2 className="mb-4">Results & Outcomes</h2>

        <p className="text-muted mb-4">
          Let's identify the specific, measurable, and emotionally compelling results your customers achieve.
        </p>

        {error && <Alert variant="danger">{error}</Alert>}

        <Alert variant="info">
          <strong>How this works:</strong><br />
          1. AI will generate 10-15 potential results based on your business foundation<br />
          2. Check ALL the results that are TRUE for your customers (could be 4, could be 12!)<br />
          3. Optionally edit any text before saving<br />
          4. AI will select the strongest results for each context (headlines, sales pages, A/B tests, etc.)
        </Alert>

        <Card className="mb-4 border-primary">
          <Card.Body>
            <h6 className="mb-3">You'll get a mix of:</h6>
            <Row>
              <Col md={6}>
                <p className="mb-2"><strong>Tangible Outcomes</strong></p>
                <ul className="small">
                  <li>What they'll have or do</li>
                  <li>Specific deliverables completed</li>
                  <li>Time saved on tasks</li>
                  <li>Clarity gained</li>
                </ul>
              </Col>
              <Col md={6}>
                <p className="mb-2"><strong>Transformational Outcomes</strong></p>
                <ul className="small">
                  <li>How they'll feel</li>
                  <li>Who they'll become</li>
                  <li>Confidence gained</li>
                  <li>Stress reduced</li>
                </ul>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <div className="d-flex justify-content-between">
          <Button
            variant="outline-secondary"
            onClick={() => navigate('/dashboard/foundation')}
          >
            Save for Later
          </Button>
          <Button
            variant="primary"
            onClick={handleGenerate}
            disabled={loading}
          >
            {loading ? 'Generating...' : 'Generate Results'}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );

  const renderGenerating = () => (
    <Card className="shadow">
      <Card.Body className="p-5 text-center">
        <Spinner animation="border" variant="primary" className="mb-3" />
        <h4>Generating potential results...</h4>
        <p className="text-muted">
          This may take 30-60 seconds.
        </p>
      </Card.Body>
    </Card>
  );

  const renderSelect = () => (
    <Card className="shadow">
      <Card.Body className="p-4">
        <h2 className="mb-4">Select Your Results</h2>

        <Alert variant="info" className="mb-4">
          <strong>Instructions:</strong> Check ALL results that are TRUE for your customers.
          You can edit any text before saving. AI will select the strongest ones for each marketing context.
        </Alert>

        {error && <Alert variant="danger">{error}</Alert>}

        <div className="mb-3">
          <Badge bg={selectedIndices.length >= 1 ? 'success' : 'secondary'}>
            {selectedIndices.length} selected
          </Badge>
        </div>

        {generatedResults.map((item, index) => {
          const selected = selectedIndices.includes(index);

          return (
            <Card
              key={index}
              className={`mb-3 ${selected ? 'border-primary' : ''}`}
            >
              <Card.Body>
                <Form.Check
                  type="checkbox"
                  id={`result-${index}`}
                  checked={selected}
                  onChange={() => handleToggleResult(index)}
                  label={
                    <span>
                      <strong className="me-2">{index + 1}.</strong>
                      {item.persona && (
                        <Badge bg="secondary" className="me-2">
                          {item.persona}
                        </Badge>
                      )}
                      <Badge bg={item.type === 'tangible' ? 'info' : 'warning'} className="me-2">
                        {item.type === 'tangible' ? 'Tangible' : 'Transformational'}
                      </Badge>
                    </span>
                  }
                  className="mb-2"
                />
                {selected ? (
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={editedTexts[index] !== undefined ? editedTexts[index] : item.result}
                    onChange={(e) => handleEditResult(index, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="mt-2"
                  />
                ) : (
                  <p className="mb-0 ms-4 text-muted">{item.result}</p>
                )}
              </Card.Body>
            </Card>
          );
        })}

        <div className="d-flex justify-content-between mt-4">
          <Button
            variant="outline-secondary"
            onClick={handleRegenerate}
            disabled={loading}
          >
            Regenerate All
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={loading || selectedIndices.length < 1}
          >
            {loading ? 'Saving...' : `Save ${selectedIndices.length} Result${selectedIndices.length !== 1 ? 's' : ''}`}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );

  const handleEditSaved = (index) => {
    setEditingIndex(index);
    setEditingText(savedResults[index]);
  };

  const handleSaveEdit = async () => {
    const updatedResults = [...savedResults];
    updatedResults[editingIndex] = editingText;

    setLoading(true);
    const result = await resultsAlignmentService.saveResults(updatedResults);
    setLoading(false);

    if (result.success) {
      setSavedResults(updatedResults);
      setEditingIndex(null);
      setEditingText('');
    } else {
      setError(result.error);
    }
  };

  const handleDeleteResult = async (index) => {
    const updatedResults = savedResults.filter((_, i) => i !== index);

    if (updatedResults.length === 0) {
      setError('You must have at least one result');
      return;
    }

    setLoading(true);
    const result = await resultsAlignmentService.saveResults(updatedResults);
    setLoading(false);

    if (result.success) {
      setSavedResults(updatedResults);
    } else {
      setError(result.error);
    }
  };

  const renderComplete = () => (
    <Card className="shadow">
      <Card.Body className="p-4">
        <h2 className="mb-4">Results & Outcomes Complete</h2>

        <Alert variant="success">
          You've selected {savedResults.length} results that authentically represent what your customers achieve!
        </Alert>

        {error && <Alert variant="danger">{error}</Alert>}

        <Card className="mb-4 border-success">
          <Card.Body>
            <h5 className="mb-3">Your Selected Results:</h5>
            {savedResults.map((result, index) => (
              <div key={index} className="mb-3 pb-3 border-bottom">
                {editingIndex === index ? (
                  <div>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={editingText}
                      onChange={(e) => setEditingText(e.target.value)}
                      className="mb-2"
                    />
                    <Button size="sm" variant="success" onClick={handleSaveEdit} disabled={loading} className="me-2">
                      {loading ? 'Saving...' : 'Save'}
                    </Button>
                    <Button size="sm" variant="outline-secondary" onClick={() => setEditingIndex(null)}>
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className="d-flex justify-content-between align-items-start">
                    <p className="mb-0 flex-grow-1">{result}</p>
                    <div className="ms-3 text-nowrap">
                      <Button size="sm" variant="outline-primary" onClick={() => handleEditSaved(index)} className="me-1">
                        Edit
                      </Button>
                      <Button size="sm" variant="outline-danger" onClick={() => handleDeleteResult(index)}>
                        ×
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </Card.Body>
        </Card>

        <div className="d-flex justify-content-between">
          <Button variant="outline-secondary" onClick={() => navigate('/dashboard/foundation')}>
            Back to Dashboard
          </Button>
          <Button variant="primary" onClick={handleRegenerate}>
            Regenerate & Reselect
          </Button>
        </div>
      </Card.Body>
    </Card>
  );

  return (
    <Container className="py-5">
      <BackToDashboard module="foundation" />
      <Row className="justify-content-center">
        <Col md={10} lg={8}>
          {stage === 'start' && renderStart()}
          {stage === 'generating' && renderGenerating()}
          {stage === 'select' && renderSelect()}
          {stage === 'complete' && renderComplete()}
        </Col>
      </Row>
    </Container>
  );
}

export default ResultsAlignment;
